// Event birds: what a redeemed code actually becomes in the world
// (EVENT-CODES.md §1). A code grants an EGG, not a bird — the hatch is the
// emotional payoff the purchase is tied to, and it drops the player into the
// brooder loop.
//
// The full incubation module (candling, turning, humidity) is Phase 8. This is
// the honest MVP version: the egg sits under a brooder lamp and hatches on a
// timer, and Phase 8 replaces `stepEventEggs` without touching anything else.

import { newId } from '../core/initialState';
import type { Rng } from '../core/rng';
import type { ChickenEntity, EventEgg, GameState, Genotype } from '../core/types';
import { createChick } from '../core/initialState';
import type { EggPayload, GrantPayload } from './eventCodes';
import { eggShellColor } from './genetics';
import { effectsOf } from './items';
import { layoutStructures } from './structureLayout';
import { stageForAge } from './lifecycle';

/** Real incubation is 21 days; the MVP egg uses the same number in sim-days. */
export const INCUBATION_DAYS = 21;

export function eventEggFromPayload(payload: EggPayload, state: GameState, rng: Rng): EventEgg {
  return {
    id: newId('egg', rng),
    breed: payload.breed,
    eventId: payload.eventId,
    eventLabel: payload.eventLabel,
    tier: payload.tier,
    genetics: payload.genetics ?? {},
    accentColor: payload.accentColor,
    receivedAtHour: state.simHour,
    // Nothing incubates until the player puts it under a lamp.
    incubatingSinceHour: undefined,
  };
}

export function addEventEgg(state: GameState, egg: EventEgg): void {
  state.eventEggs = [...(state.eventEggs ?? []), egg];
}

/** Age a bird starts at for each grantable stage — the middle of each band. */
const STAGE_AGE_DAYS: Record<'chick' | 'pullet' | 'adult', number> = {
  chick: 14,
  pullet: 70,
  adult: 154,
};

export type GrantResult =
  | { kind: 'egg'; egg: EventEgg }
  | { kind: 'bird'; bird: ChickenEntity }
  | { kind: 'item'; itemId: string; amount: number }
  | { kind: 'eggits'; amount: number };

/**
 * Turns a redeemed code into whatever it promised. Everything a code can grant
 * lands here, so the redemption UI has exactly one thing to render and the
 * server stays the only authority on what was granted.
 */
export function applyGrant(state: GameState, grant: GrantPayload, rng: Rng): GrantResult {
  if (grant.kind === 'eggits') {
    state.eggits += grant.amount;
    return { kind: 'eggits', amount: grant.amount };
  }

  if (grant.kind === 'item') {
    // Straight into the Build/Buy inventory — granted, so no Eggits change
    // hands. `buyItem` would charge for it.
    state.inventory[grant.itemId] = (state.inventory[grant.itemId] ?? 0) + grant.amount;
    return { kind: 'item', itemId: grant.itemId, amount: grant.amount };
  }

  const egg = eventEggFromPayload(grant, state, rng);
  if ((grant.stage ?? 'egg') === 'egg') {
    addEventEgg(state, egg);
    return { kind: 'egg', egg };
  }

  // A card that says "baby chick" hands over a chick. Skipping incubation is
  // the whole point of the stage grant, so the bird is built the same way a
  // hatch builds it — same genetics, same provenance badge — just older.
  const bird = birdFromEgg(state, egg, rng, grant.stage as 'chick' | 'pullet' | 'adult');
  state.flock.push(bird);
  return { kind: 'bird', bird };
}

/** Builds the granted bird at a stage, reusing the hatch path's trait logic. */
function birdFromEgg(
  state: GameState,
  egg: EventEgg,
  rng: Rng,
  stage: 'chick' | 'pullet' | 'adult',
): ChickenEntity {
  const bird = hatchEventEgg(state, egg, rng);
  bird.ageInDays = STAGE_AGE_DAYS[stage];
  bird.stage = stageForAge(bird.ageInDays);
  return bird;
}

/** Puts the egg under the brooder lamp. Without a brooder, nothing happens. */
export function startIncubation(state: GameState, eggId: string): boolean {
  const egg = state.eventEggs?.find((e) => e.id === eggId);
  if (!egg || egg.incubatingSinceHour !== undefined) return false;
  const hasBrooder = state.structures.some((s) => effectsOf(s).brooder);
  if (!hasBrooder) return false;
  egg.incubatingSinceHour = state.simHour;
  return true;
}

export function hoursUntilHatch(egg: EventEgg, simHour: number): number | null {
  if (egg.incubatingSinceHour === undefined) return null;
  return Math.max(0, egg.incubatingSinceHour + INCUBATION_DAYS * 24 - simHour);
}

/**
 * Applies the code's guaranteed traits over a normal genotype for the breed.
 * "Guaranteed special genetics" is the collectible's whole promise, so these
 * override the roll rather than nudging it.
 */
export function applyEventGenetics(genotype: Genotype, egg: EventEgg): Genotype {
  const next: Genotype = structuredClone(genotype);
  if (egg.genetics.carriesBlueEgg) {
    // One copy is enough — blue is dominant, which is the lesson on the card.
    next.eggColor = [next.eggColor[0] === 'O' ? 'O' : 'O', next.eggColor[1]];
  }
  if (egg.genetics.silkieFeathered) next.plumage = ['f', 'f'];
  if (egg.genetics.fiveToed) next.toes = ['P', 'P'];
  return next;
}

/** Hatches any egg whose timer has run out. Returns the new chicks. */
export function stepEventEggs(state: GameState, rng: Rng): ChickenEntity[] {
  const eggs = state.eventEggs ?? [];
  if (eggs.length === 0) return [];

  const hatched: ChickenEntity[] = [];
  const remaining: EventEgg[] = [];

  for (const egg of eggs) {
    const due = hoursUntilHatch(egg, state.simHour);
    if (due === null || due > 0) {
      remaining.push(egg);
      continue;
    }
    hatched.push(hatchEventEgg(state, egg, rng));
  }

  state.eventEggs = remaining;
  for (const chick of hatched) state.flock.push(chick);
  return hatched;
}

export function hatchEventEgg(state: GameState, egg: EventEgg, rng: Rng): ChickenEntity {
  const brooder = state.structures.find((s) => effectsOf(s).brooder);
  const spot = brooder ? layoutStructures(state).get(brooder.id) : undefined;
  const chick = createChick(
    rng,
    egg.breed,
    eventChickName(egg, rng),
    {
      x: spot?.x ?? 0,
      y: spot?.y ?? 0,
    },
    14,
    'hen',
    Math.floor(state.simHour / 24),
  );
  chick.genotype = applyEventGenetics(chick.genotype, egg);
  // The permanent badge: this bird's origin shows in its info panel forever.
  chick.provenance = { eventId: egg.eventId, label: egg.eventLabel };
  return chick;
}

const EVENT_NAMES = ['Coquí', 'Flor', 'Guaraguao', 'Maví', 'Ceiba', 'Yagrumo', 'Piña'];

function eventChickName(egg: EventEgg, rng: Rng): string {
  void egg;
  return EVENT_NAMES[rng.int(0, EVENT_NAMES.length - 1)];
}

/** The shell colour this egg will show in the UI — a genetics lesson by itself. */
export function eventEggShell(egg: EventEgg): string {
  if (egg.genetics.carriesBlueEgg) return 'blue';
  return eggShellColor({
    plumage: ['F', 'F'],
    eggColor: ['o', 'o'],
    toes: ['p', 'p'],
    temperament: ['D', 'D'],
    broodiness: ['b', 'b'],
    brownCoating: egg.breed === 'rhode_island_red' ? 70 : 10,
  });
}
